import { useState, useCallback, useMemo } from 'react';
import { ReferralData } from '../types';

interface UseReferralDataProps {
  currentUser: string;
  maxLockedRecords?: number;
}

export const useReferralData = ({ currentUser, maxLockedRecords = 5 }: UseReferralDataProps) => {
  const [data, setData] = useState<ReferralData[]>([]);

  const lockedRecordsCount = useMemo(() => {
    return data.filter((item) => item.lockedBy === currentUser).length;
  }, [data, currentUser]);

  const handleSelectRow = useCallback(
    (id: string) => {
      setData((prevData) =>
        prevData.map((item) => {
          if (item.id === id && item.lockedBy && item.lockedBy !== currentUser) {
            return item; // Don't allow selecting/deselecting records locked by others
          }
          return item.id === id ? { ...item, selected: !item.selected } : item;
        })
      );
    },
    [currentUser]
  );

  const handleSelectAll = useCallback(
    (checked: boolean, visibleIds: string[]) => {
      setData((prevData) =>
        prevData.map((item) => {
          if (item.lockedBy && item.lockedBy !== currentUser) {
            return item; // Don't allow selecting/deselecting records locked by others
          }
          return visibleIds.includes(item.id) ? { ...item, selected: checked } : item;
        })
      );
    },
    [currentUser]
  );

  const handlePatientCCIdChange = useCallback((id: string, newValue: string) => {
    setData((prevData) =>
      prevData.map((item) => (item.id === id ? { ...item, patientCCId: newValue } : item))
    );
  }, []);

  const handleStatusChange = useCallback((id: string, newValue: string) => {
    setData((prevData) =>
      prevData.map((item) => (item.id === id ? { ...item, status: newValue } : item))
    );
  }, []);

  const handleLockToggle = useCallback(
    (id: string) => {
      setData((prevData) => {
        const record = prevData.find((item) => item.id === id);
        if (!record) return prevData;

        // Check if trying to lock and already at max limit
        if (!record.lockedBy && lockedRecordsCount >= maxLockedRecords) {
          alert(`Maximum ${maxLockedRecords} records can be locked at a time`);
          return prevData;
        }

        // Toggle lock status
        return prevData.map((item) => {
          if (item.id === id) {
            if (item.lockedBy === currentUser) {
              // Unlock
              return { ...item, lockedBy: null, lockedAt: null };
            } else if (!item.lockedBy) {
              // Lock by current user
              const now = new Date();
              const formattedDate = `${String(now.getDate()).padStart(2, '0')}/${String(
                now.getMonth() + 1
              ).padStart(2, '0')}/${now.getFullYear()} ${String(now.getHours()).padStart(
                2,
                '0'
              )}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(
                2,
                '0'
              )}`;
              return { ...item, lockedBy: currentUser, lockedAt: formattedDate };
            }
          }
          return item;
        });
      });
    },
    [currentUser, lockedRecordsCount, maxLockedRecords]
  );

  return {
    data,
    setData,
    lockedRecordsCount,
    handleSelectRow,
    handleSelectAll,
    handlePatientCCIdChange,
    handleStatusChange,
    handleLockToggle,
  };
};
